import { KeyValuePair } from 'utils/array';
import { newGuid } from 'utils/guid';

export enum CopilotConversionItemType {
  User = 0,
  Copilot = 1,
  CopilotQuestion = 2,
  Document = 3,
  CopilotAnswerWithLink = 5,
  SharepointIntegrations = 6,
  // SkillControl cannot be used yet (phase 22): a client-side card with what to do (sign in, ask an administrator)
  SkillControlAccess = 7,
}

export enum CopilotManagerPromptType {
  Custom = 0,
  Context = 1,
  General = 2,
}

export enum CopilotErrorType {
  MaxTokens = 1,
}

export enum ContentBlockKind {
  Document = 1,
  Markdown = 2,
  DataTable = 3,
  AdaptiveCard = 4,
  Link = 5,
  // a typed question set an agent asks (phase 32): payload carries the questions, the card writes the answers json into adaptiveInputs
  QuestionSet = 6,
  // a file made for the user, a tool's workbook or a model output: payload is a CopilotFileBlockPayload, mimeType its content type
  File = 7,
  // a notice the answer carries for the user: text holds its lines (the files a model produced that were left out, with why)
  Notice = 8,
  // a chart the answer carries: the payload is the chart definition, the client draws it (copilotChartBlock.ts)
  Chart = 9,
  // the context relations the task assistant proposes for the open task: the payload is a CopilotContextProposalPayloadDTO
  // (mapFromCopilotContextProposalPayload), the task modal applies the items to its unsaved task
  ContextProposal = 10,
}

export const CopilotLibraryDocumentEditorAction = 'openLibraryDocumentEditor';

export interface CopilotLibraryDocumentEditorRequest {
  libraryLinkId: number;
  libraryListId?: number;
  title?: string;
  pendingInstruction?: string;
  handoffBlockId?: string;
}

export class CopilotConversationContentBlock {
  id?: string;

  kind: ContentBlockKind;

  version: number;

  created?: Date;

  title?: string;

  text?: string;

  link?: string;

  savedLibraryListId?: number;

  savedLibraryLinkId?: number;

  adaptiveCardJson?: string;

  dataTable?: CopilotDataTableContent;

  adaptiveInputs?: Record<string, string>;

  payload?: unknown;

  mimeType?: string;

  sourceUrl?: string;

  constructor() {
    this.kind = ContentBlockKind.Markdown;
    this.version = 0;
  }

  clone(): CopilotConversationContentBlock {
    const clone = new CopilotConversationContentBlock();
    clone.id = this.id;
    clone.kind = this.kind;
    clone.version = this.version;
    clone.created = this.created ? new Date(this.created) : undefined;
    clone.title = this.title;
    clone.text = this.text;
    clone.link = this.link;
    clone.savedLibraryListId = this.savedLibraryListId;
    clone.savedLibraryLinkId = this.savedLibraryLinkId;
    clone.adaptiveCardJson = this.adaptiveCardJson;
    clone.dataTable = this.dataTable?.clone();
    clone.adaptiveInputs = this.adaptiveInputs ? { ...this.adaptiveInputs } : undefined;
    clone.payload = this.payload;
    clone.mimeType = this.mimeType;
    clone.sourceUrl = this.sourceUrl;

    return clone;
  }
}

export class CopilotDataTableContent {
  columns: CopilotDataTableColumn[];

  rows: Record<string, unknown>[];

  page: number;

  pageSize: number;

  totalCount: number;

  returnedCount: number;

  isTruncated: boolean;

  maxReturnedRows: number;

  constructor() {
    this.columns = [];
    this.rows = [];
    this.page = 0;
    this.pageSize = 0;
    this.totalCount = 0;
    this.returnedCount = 0;
    this.isTruncated = false;
    this.maxReturnedRows = 0;
  }

  clone(): CopilotDataTableContent {
    const clone = new CopilotDataTableContent();
    clone.columns = this.columns.map((column) => column.clone());
    clone.rows = this.rows.map((row) => ({ ...row }));
    clone.page = this.page;
    clone.pageSize = this.pageSize;
    clone.totalCount = this.totalCount;
    clone.returnedCount = this.returnedCount;
    clone.isTruncated = this.isTruncated;
    clone.maxReturnedRows = this.maxReturnedRows;

    return clone;
  }
}

export class CopilotDataTableColumn {
  key: string;

  name: string;

  dataType?: string;

  constructor() {
    this.key = '';
    this.name = '';
  }

  clone(): CopilotDataTableColumn {
    const clone = new CopilotDataTableColumn();
    clone.key = this.key;
    clone.name = this.name;
    clone.dataType = this.dataType;

    return clone;
  }
}

export class CopilotConversation {
  id?: string;

  request?: CopilotConversationItem;

  thread: CopilotConversationItem[];

  // a loaded session that waits on a question card (AITaskSessionStatus.WaitingForAnswer): the card's block id
  pendingQuestionBlockId?: string;

  constructor() {
    this.thread = [];
  }

  hasData = (): boolean => {
    return this.thread.length > 0;
  };

  setRequestFromQuery =(query: string): CopilotConversationItem => {
    this.request = new CopilotConversationItem();
    this.request.question = query;
    this.request.type = CopilotConversionItemType.User;
    this.request.promptType = CopilotManagerPromptType.Custom;

    return this.request;
  };

  setRequestFromEntitySelection = (selection: number[], question?: string): CopilotConversationItem => {
    this.request = new CopilotConversationItem();
    this.request.question = question;
    this.request.type = CopilotConversionItemType.User;
    this.request.promptType = CopilotManagerPromptType.Custom;
    this.request.selectedEntityIds = selection;

    return this.request;
  };

  clone = (): CopilotConversation => {
    const clone = new CopilotConversation();
    clone.id = this.id;
    clone.request = this.request?.clone();
    clone.thread = this.thread?.map((item) => item.clone());
    clone.pendingQuestionBlockId = this.pendingQuestionBlockId;

    return clone;
  };
}

export class CopilotResponseAnnotations {
  citations?: PromptResponseCitation[];

  metadata?: KeyValuePair[];

  hasData = (): boolean => {
    return !!(this.citations && this.citations.length > 0) || !!(this.metadata && this.metadata.length > 0);
  };
}

export class CopilotConversationBaseItem extends CopilotResponseAnnotations {
  question?: string;

  context?: string;

  promptType?: CopilotManagerPromptType;

  response?: string;

  responseRaw?: string;

  selectedEntityIds?: number[];

  link?: string;

  contentBlocks?: CopilotConversationContentBlock[];

  action?: () => Promise<void>;

  // Run `action` as soon as the item mounts and finds its preconditions met (the SharePoint card re-opened
  // after a consent redirect). An item created by a refusal leaves this off: it only acts after the user
  // changed something in it, so a refused start can never loop into an automatic restart.
  actionOnMount?: boolean;

  // SkillControlAccess only: the refusal's code (MCPCONN0002, 0004, 0005, 0006 or 0007), which decides what the card offers
  skillControlRefusal?: string;
}

export class CopilotConversationItem extends CopilotConversationBaseItem {
  id?: string;

  type: CopilotConversionItemType;

  date?: Date;

  userId?: string;

  copilotStatus?: string;

  constructor() {
    super();
    this.id = newGuid();
    this.date = new Date();
    this.type = CopilotConversionItemType.User;
  }

  clone(): CopilotConversationItem {
    const clone = new CopilotConversationItem();
    clone.id = this.id;
    clone.question = this.question;
    clone.context = this.context;
    clone.promptType = this.promptType;
    clone.response = this.response;
    clone.responseRaw = this.responseRaw;
    clone.type = this.type;
    clone.date = this.date ? new Date(this.date) : undefined;
    clone.userId = this.userId;
    clone.copilotStatus = this.copilotStatus;
    clone.citations = this.citations?.map((c) => c.clone()) || [];
    clone.metadata = this.metadata ? [...this.metadata] : undefined;
    clone.link = this.link;
    clone.selectedEntityIds = this.selectedEntityIds ? [...this.selectedEntityIds] : undefined;
    clone.contentBlocks = this.contentBlocks?.map((block) => block.clone());
    clone.action = this.action;
    clone.actionOnMount = this.actionOnMount;
    clone.skillControlRefusal = this.skillControlRefusal;

    return clone;
  }
}

export class PromptResponseCitation {
  title?: string;

  uri?: string;

  clone(): PromptResponseCitation {
    const clone = new PromptResponseCitation();
    clone.title = this.title;
    clone.uri = this.uri;

    return clone;
  }
}
